import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@/environments/environment';
import { BaseResponseDataModel } from '../interface/base-response.interface';
import {
  AddOrderTemplateToCartRequest,
  AddOrderTemplateToCartResponse,
  CloneOrderTemplateRequest,
  CreateOrderTemplateFromCartRequest,
  CreateOrderTemplateFromOrderRequest,
  CreateOrderTemplateRequest,
  OrderTemplateListResponse,
  OrderTemplateResponse,
  UpdateOrderTemplateRequest
} from '../interface/order-template.interface';

@Injectable({
  providedIn: 'root'
})
export class OrderTemplateService {
  private readonly baseUrl = `${environment.baseURL}order/OrderTemplate`;

  constructor(private http: HttpClient) {}

  getOrderTemplates(
    offset: number = 0,
    limit: number = 20,
    searchTerm: string = '',
    source: string = ''
  ): Observable<BaseResponseDataModel<OrderTemplateListResponse>> {
    let params = new HttpParams()
      .set('offset', offset.toString())
      .set('limit', limit.toString());

    if (searchTerm && searchTerm.trim().length > 0) {
      params = params.set('searchTerm', searchTerm.trim());
    }

    if (source && source.trim().length > 0) {
      params = params.set('source', source.trim());
    }

    return this.http.get<BaseResponseDataModel<OrderTemplateListResponse>>(this.baseUrl, {
      params
    });
  }

  getOrderTemplateDetail(id: string): Observable<BaseResponseDataModel<OrderTemplateResponse>> {
    return this.http.get<BaseResponseDataModel<OrderTemplateResponse>>(`${this.baseUrl}/${id}`);
  }

  createOrderTemplate(
    request: CreateOrderTemplateRequest
  ): Observable<BaseResponseDataModel<OrderTemplateResponse>> {
    return this.http.post<BaseResponseDataModel<OrderTemplateResponse>>(this.baseUrl, request, {
      headers: { 'Content-Type': 'application/json' }
    });
  }

  createFromCart(
    request: CreateOrderTemplateFromCartRequest
  ): Observable<BaseResponseDataModel<OrderTemplateResponse>> {
    return this.http.post<BaseResponseDataModel<OrderTemplateResponse>>(
      `${this.baseUrl}/from-cart`,
      request,
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );
  }

  createFromOrder(
    request: CreateOrderTemplateFromOrderRequest
  ): Observable<BaseResponseDataModel<OrderTemplateResponse>> {
    return this.http.post<BaseResponseDataModel<OrderTemplateResponse>>(
      `${this.baseUrl}/from-order`,
      request,
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );
  }

  updateOrderTemplate(
    id: string,
    request: UpdateOrderTemplateRequest
  ): Observable<BaseResponseDataModel<OrderTemplateResponse>> {
    return this.http.put<BaseResponseDataModel<OrderTemplateResponse>>(
      `${this.baseUrl}/${id}`,
      request,
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );
  }

  cloneOrderTemplate(
    id: string,
    request: CloneOrderTemplateRequest = {}
  ): Observable<BaseResponseDataModel<OrderTemplateResponse>> {
    return this.http.post<BaseResponseDataModel<OrderTemplateResponse>>(
      `${this.baseUrl}/${id}/clone`,
      request,
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );
  }

  deleteOrderTemplate(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  addToCart(
    id: string,
    request: AddOrderTemplateToCartRequest = {}
  ): Observable<BaseResponseDataModel<AddOrderTemplateToCartResponse>> {
    return this.http.post<BaseResponseDataModel<AddOrderTemplateToCartResponse>>(
      `${this.baseUrl}/${id}/add-to-cart`,
      request,
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );
  }
}
